import { useEffect, useRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import { fromLonLat } from 'ol/proj';
import {
  defaults as defaultControls,
  ZoomSlider,
  Control,
  Attribution,
} from 'ol/control';
import { useMap } from '../../../providers/MapProvider';
import {
  basemaps,
  createGoogleLogoControl,
} from '../components/controls/BasemapControl';
import { BasemapType, useMapStore } from '@/stores/mapStore';

export const INITIAL_VIEW_STATE = {
  center: fromLonLat([-98.5795, 39.8283]),
  zoom: 5,
};

const GOOGLE_BASEMAP_KEYS: BasemapType[] = ['googleRoadmap', 'googleSatellite'];

export const useMapInstance = (
  mapTargetRef: React.RefObject<HTMLDivElement | null>,
  onMapReady?: (map: Map) => void,
) => {
  const { map, setMap } = useMap();
  const { activeBasemap } = useMapStore();
  const initializedRef = useRef(false); // guard against re-init

  useEffect(() => {
    if (!mapTargetRef.current || initializedRef.current) return;
    initializedRef.current = true;

    const zoomLabelElement = document.createElement('div');
    zoomLabelElement.className = 'ol-zoom-label';

    const zoomLabelControl = new Control({ element: zoomLabelElement });
    const zoomSliderControl = new ZoomSlider();

    const olMap = new Map({
      target: mapTargetRef.current,
      layers: [basemaps[activeBasemap]],
      view: new View({
        center: INITIAL_VIEW_STATE.center,
        zoom: INITIAL_VIEW_STATE.zoom,
        constrainResolution: true,
        minZoom: 0,
        maxZoom: 22,
        multiWorld: true,
      }),
      controls: defaultControls({ attribution: false }).extend([
        createGoogleLogoControl(),
        zoomSliderControl,
        zoomLabelControl,
        // Expanded by default
        new Attribution({
          collapsible: true,
          collapsed: false,
        }),
      ]),
    });

    const updateZoomLabel = () => {
      const zoom = olMap.getView().getZoom();
      if (zoom !== undefined) {
        zoomLabelElement.innerText = `${Math.floor(zoom)}`;
      }
    };

    olMap.getView().on('change:resolution', updateZoomLabel);
    updateZoomLabel();

    setMap(olMap);
    onMapReady?.(olMap);

    return () => {
      olMap.setTarget(undefined);
      setMap(null);
      initializedRef.current = false;
    };
  }, [mapTargetRef]); // remove map, setMap, onMapReady from deps

  // Basemap effect unchanged — map from context is fine here
  useEffect(() => {
    if (!map) return;
    map.getLayers().setAt(0, basemaps[activeBasemap]);
    const logoElement = map
      .getTargetElement()
      ?.querySelector('.ol-google-logo');
    if (logoElement) {
      logoElement.classList.toggle(
        'hidden',
        !GOOGLE_BASEMAP_KEYS.includes(activeBasemap),
      );
    }
  }, [map, activeBasemap]);

  return map;
};
